import { configureStore } from '@reduxjs/toolkit';
import { Services } from '@packages/shared/core/common/context';
import { featureFlagsAttrObserver } from '@packages/shared/core/feature-flags/store';
import rootReducer, { HYDRATE_FROM_STORAGE, RootInitialState } from './rootReducer';
import { loadProgressFromStorage, saveProgressToStorage } from './storage';
import type { AppDispatch } from './types';

export type AppStore = ReturnType<typeof createStore>;

export function createStore(services: Services) {
  const { featureFlags, appEnvironmentService, utmTagsService } = services;

  const userUtmTags = { tags: utmTagsService.getUtmTags() };
  const system = {
    isTestingEnv: appEnvironmentService.isTestingEnv(),
    platform: appEnvironmentService.getPlatform(),
    isInAppBrowser: appEnvironmentService.isInAppBrowser(),
  };

  const preloadedState: Partial<RootInitialState> = {
    system,
    userUtmTags,
  };

  const enhancers = [featureFlagsAttrObserver(featureFlags)];

  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: services,
        },
      }),
    enhancers,
  });

  const dispatch = store.dispatch as AppDispatch;

  store.subscribe(() => {
    const progressState = store.getState();

    saveProgressToStorage(progressState);
  });

  return { ...store, dispatch };
}

export function hydrateStoreFromStorage(store: AppStore) {
  const persistedState = loadProgressFromStorage();

  if (persistedState) {
    store.dispatch({ type: HYDRATE_FROM_STORAGE, payload: persistedState });
  }
}
